import { PropsWithChildren, ReactNode } from "react";
import { Dialog, DialogContent } from "./dialog";
import { Error as ErrorIcon } from "@/assets";
import { cn } from "@/lib/utils";
import { HeadingSubsection } from "./typography";

type ErrorUIType =
    | "fullscreen" // Covers the entire viewport
    | "cover" // Covers the UI up to the nearest non-static positioned ancestor. i.e. put `relative` on the parent you want the ErrorUI boundary to stop at.
    | "block"; // Place the ErrorUI wherever you want. Behaves like a block element, i.e. a div

const ErrorUI: React.FC<{
    //
    type?: ErrorUIType;
    icon?: ReactNode;
    title?: ReactNode;
    description?: ReactNode;
    actionButtons?: ReactNode[];
    showBorder?: boolean; // only for cover
    verticallyCentred?: boolean; // only for cover
}> = ({
    type = "block",
    icon = <ErrorIcon className="h-10 w-10" />,
    title = "Oh no! Something went wrong...",
    description = "We ran into an issue.",
    showBorder = true,
    actionButtons,
    verticallyCentred = false,
}) => {
    return (
        <Wrapper type={type} showBorder={showBorder}>
            <div
                className={cn(
                    verticallyCentred && "h-full",
                    "px-wide grid content-center justify-items-center",
                )}
            >
                <span className="text-icon-danger">{icon}</span>
                <HeadingSubsection className="text-center">
                    {title}
                </HeadingSubsection>
                <span className="font-bolder text-center">{description}</span>
                {actionButtons && (
                    <div className="mt-wide gap-default flex">
                        {actionButtons}
                    </div>
                )}
            </div>
        </Wrapper>
    );
};

const Wrapper: React.FC<
    PropsWithChildren<{ type: ErrorUIType; showBorder: boolean }>
> = ({ type, showBorder, children }) => {
    if (type === "fullscreen") {
        return (
            <Dialog open>
                <DialogContent
                    className="border-danger-bold"
                    overlayClassname="bg-error"
                >
                    {children}
                </DialogContent>
            </Dialog>
        );
    } else if (type === "cover") {
        return (
            <div
                className={cn(
                    "bg-error absolute inset-0 z-10 grid backdrop-blur-xs",
                    showBorder &&
                        "rounded-default ring-ring-danger ring-1 ring-inset",
                )}
            >
                {children}
            </div>
        );
    } else {
        return (
            <div
                className={cn(
                    showBorder &&
                        "rounded-default ring-ring-danger p-wider ring-1 ring-inset",
                )}
            >
                {children}
            </div>
        );
    }
};

export default ErrorUI;
